import { useState, type FormEvent } from 'react';
import type { ContactDto } from '@chat-emp/shared';

import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { StartChatDialog } from './StartChatDialog.js';
import { messageOf, type UseContacts } from './useContacts.js';
import styles from './ContactsPanel.module.css';

interface ContactsPanelProps {
  book: UseContacts;
  /** A chat was opened with a contact; the view selects it. */
  onStarted: (channelId: string) => void;
}

// A shape check only: chat-kit owns the real address rules and its refusal is shown.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The employer's contact book: add, rename, delete, and start an email chat. */
export function ContactsPanel({ book, onStarted }: ContactsPanelProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [problem, setProblem] = useState<string>();
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [starting, setStarting] = useState<ContactDto | null>(null);
  const [busy, setBusy] = useState(false);

  /** One write at a time; the controls are disabled meanwhile, Enter in a field is dropped. */
  async function attempt(work: () => Promise<unknown>): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setProblem(undefined);
    try {
      await work();
      return true;
    } catch (err) {
      setProblem(messageOf(err, 'That did not work. Try again.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add(event: FormEvent) {
    event.preventDefault();
    const body = { name: name.trim(), email: email.trim() };
    if (!body.name) return setProblem('Enter a name.');
    if (!EMAIL.test(body.email)) return setProblem('Enter a valid email address.');
    if (await attempt(() => book.save(body))) {
      setName('');
      setEmail('');
    }
  }

  async function rename(contact: ContactDto, next: string) {
    const trimmed = next.trim();
    if (!trimmed) return setProblem('Enter a name.');
    if (trimmed === contact.name) return setRenaming(null);
    if (await attempt(() => book.save({ id: contact.id, name: trimmed, email: contact.email }))) {
      setRenaming(null);
    }
  }

  function list() {
    if (book.error) {
      return (
        <EmptyState
          title="Couldn't load contacts"
          description={book.error}
          action={{ label: 'Try again', onClick: book.reload }}
        />
      );
    }
    if (!book.loading && book.contacts.length === 0) {
      return (
        <EmptyState
          icon={<Icon name="users" size={32} />}
          title="No contacts yet"
          description="Add one to start a chat."
        />
      );
    }
    return (
      <ul className={styles.rows}>
        {book.contacts.map((contact) => (
          <li key={contact.id} className={styles.row}>
            {renaming?.id === contact.id ? (
              <input
                className={styles.input}
                aria-label={`New name for ${contact.name}`}
                value={renaming.name}
                onChange={(event) => setRenaming({ id: contact.id, name: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                    void rename(contact, renaming.name);
                  }
                  if (event.key === 'Escape') setRenaming(null);
                }}
                autoFocus
              />
            ) : (
              <span className={styles.who}>
                <span className={styles.name}>{contact.name}</span>
                <span className={styles.email}>{contact.email}</span>
              </span>
            )}
            <span className={styles.actions}>
              <button
                className={styles.iconBtn}
                aria-label={`Start chat with ${contact.name}`}
                title="Start chat"
                disabled={busy}
                onClick={() => setStarting(contact)}
              >
                <Icon name="chatBubble" size={15} />
              </button>
              <button
                className={styles.iconBtn}
                aria-label={`Rename ${contact.name}`}
                title="Rename"
                disabled={busy}
                onClick={() => setRenaming({ id: contact.id, name: contact.name })}
              >
                <Icon name="pencil" size={15} />
              </button>
              <button
                className={styles.iconBtn}
                aria-label={`Delete ${contact.name}`}
                title="Delete"
                disabled={busy}
                onClick={() => void attempt(() => book.remove(contact.id))}
              >
                <Icon name="trash" size={15} />
              </button>
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h2 className={styles.title}>Contacts</h2>
      </div>
      <form
        className={styles.add}
        aria-label="Add a contact"
        onSubmit={(event) => void add(event)}
        noValidate
      >
        <input
          className={styles.input}
          aria-label="Name"
          placeholder="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <input
          className={styles.input}
          aria-label="Email"
          placeholder="Email"
          inputMode="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button type="submit" disabled={busy}>
          Add
        </Button>
      </form>
      {problem && (
        <p className={styles.error} role="alert">
          {problem}
        </p>
      )}
      <div className={styles.list} aria-busy={book.loading || undefined}>
        {list()}
      </div>
      {starting && (
        <StartChatDialog
          contact={starting}
          onClose={() => setStarting(null)}
          onStarted={(channelId) => {
            setStarting(null);
            onStarted(channelId);
          }}
        />
      )}
    </div>
  );
}
